import {Component, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {Subscription} from 'rxjs';
import {FormBuilder, FormGroup} from '@angular/forms';
import {
  createFormBuilderDTO,
  createFormBuilderForm,
  FormBuilderDTO, FormBuilderModel
} from '../../../components/form-builder-list/form-builder/store/form-builder.model';
import {Column} from '../../../../../shared/modules/data-table/models/mbl-table.model';
import {ChoiceInputItem} from '../../../../../shared/modules/form-elements/models/form-elements.model';
import {choiceInputItems, themeChoices, styleChoices} from '../../../store/dynamic-form.model';
import {NgbModalOptions} from '@ng-bootstrap/ng-bootstrap';
import {ActivatedRoute, Router} from '@angular/router';
import {ModalService} from '../../../../../shared/modules/shared-common/services/modal/modal.service';
import {AlertService} from '../../../../../shared/modules/alert/services/alert.service';
import {FormBuilderService} from '../../../components/form-builder-list/form-builder/store/form-builder.service';
import {
  FormBuilderQuestionService
} from '../../../components/form-builder-list/form-builder/components/form-builder-question/store/form-builder-question.service';
import {FormBuilderQuery} from '../../../components/form-builder-list/form-builder/store/form-builder.query';
import {SessionQuery} from '../../../../../shared/stores/session/session.query';
import {take} from 'rxjs/operators';
import {
  FormBuilderQuestionDTO
} from '../../../components/form-builder-list/form-builder/components/form-builder-question/store/form-builder-question.model';
import {
  FormBuilderQuestionComponent
} from '../../../components/form-builder-list/form-builder/components/form-builder-question/form-builder-question.component';
import {booleanChoices} from 'app/modules/constants/boolean-answers.const';

@Component({
  selector: 'app-template-form-builder',
  templateUrl: './template-form-builder.component.html',
  styleUrls: ['./template-form-builder.component.scss']
})
export class TemplateFormBuilderComponent implements OnInit, OnDestroy {

  @ViewChild('requiredTmpl', {static: true}) requiredTmpl: TemplateRef<any>;
  @ViewChild('controlTypeTmpl', {static: true}) controlTypeTmpl: TemplateRef<any>;
  @ViewChild('actionTmpl', {static: true}) actionTmpl: TemplateRef<any>;

  formId: number;
  loading = true;
  formBuilderSubscription: Subscription;
  formBuilderForm: FormGroup;
  formEntity: FormBuilderDTO;
  isEditable = true;

  columns: Column[];

  themeChoices: ChoiceInputItem[] = themeChoices;
  styleChoices: ChoiceInputItem[] = styleChoices;
  booleanChoices: ChoiceInputItem[] = booleanChoices;
  controlTypeChoices: ChoiceInputItem[] = choiceInputItems;

  activeSubscription: Subscription;
  componentInstanceSubscription: Subscription;

  options: NgbModalOptions;

  constructor(private route: ActivatedRoute,
              private router: Router,
              private modalService: ModalService,
              private alertService: AlertService,
              private formBuilder: FormBuilder,
              private formBuilderService: FormBuilderService,
              private formBuilderQuestionService: FormBuilderQuestionService,
              private formBuilderQuery: FormBuilderQuery,
              private sessionQuery: SessionQuery) {
  }

  ngOnInit() {
    const id = this.route.snapshot.params.formBuilderId;
    if (id !== 'new') {
      this.formId = Number(id);
    }
    this.populateColumns();

    this.options = {
      // ariaLabelledBy: `${(this.isCreating ? 'Add' : 'Edit')} Question`,
      centered: true,
      size: 'xl',
      scrollable: true,
    };

    this.fetch();
  }

  ngOnDestroy() {
    if (this.formBuilderSubscription) {
      this.formBuilderSubscription.unsubscribe();
    }
    if (this.activeSubscription) {
      this.activeSubscription.unsubscribe();
    }
    if (this.componentInstanceSubscription) {
      this.componentInstanceSubscription.unsubscribe();
    }
  }

  private populateColumns() {
    this.columns = [
      {name: 'Order', prop: 'order', width: '15%', sort: true},
      {name: 'Key/Id', prop: 'key', width: '15%', sort: true},
      {name: 'Label', prop: 'label', width: '15%', sort: true},
      {name: 'Mandatory', prop: 'required', width: '15%', sort: true, cellTemplate: this.requiredTmpl},
      {name: 'Type', prop: 'controlType', width: '15%', cellTemplate: this.controlTypeTmpl},
      {name: 'Action', prop: 'action', width: '5%', cellTemplate: this.actionTmpl},
    ];
  }

  private fetch() {
    this.loading = true;

    if (this.formId) {
      this.activeSubscription = this.formBuilderQuery.getActiveObservable().subscribe(item => {
        if (item) {
          if (this.formBuilderForm) {
            this.formBuilderForm.patchValue(item);
          } else {
            this.formBuilderForm = createFormBuilderForm(this.formBuilder, item);
          }
          this.formEntity = item;
          this.isEditable = item.isEditable;
        } else {
          const params = this.getParams();
          if (!this.formBuilderForm) {
            this.formBuilderForm = createFormBuilderForm(this.formBuilder, params);
          }
          this.formEntity = createFormBuilderDTO(params);
        }
      }, () => {
        this.alertService.setAlert('Failed to fetch Form', 'error', 'formBuilder');
      });

      this.formBuilderSubscription = this.formBuilderService.getOne(this.formId)
        .pipe(take(1)).subscribe(() => {
          this.loading = false;
        }, () => {
          this.loading = false;
          this.alertService.setAlert(`Failed to get form with id ${this.formId}`, 'error', 'formBuilder');
        });
    } else {
      const params = this.getParams();
      this.formBuilderForm = createFormBuilderForm(this.formBuilder, params);
      this.formEntity = createFormBuilderDTO(params);
      this.loading = false;
    }
  }

  save(formBuilderForm: FormGroup) {
    this.loading = true;
    if (formBuilderForm.valid) {
      if (this.formId) {
        this.update(formBuilderForm.value);
      } else {
        this.create(formBuilderForm.value);
      }
    } else {
      this.alertService.setAlert('Please complete all fields', 'error', 'formBuilder');
    }
  }

  private update(value: FormBuilderModel) {
    this.formBuilderService.update(value).subscribe(() => {
      this.loading = false;
      this.fetch();
    }, () => {
      this.alertService.setAlert('Failed to update Form', 'error', 'formBuilder');
    });
  }

  private create(value: FormBuilderModel) {
    this.formBuilderService.create(value).subscribe(response => {
      this.loading = false;
      this.formId = response.id;
      this.fetch();
      this.router.navigateByUrl(`/form-builder/template/details/${response.id}`).then();
    }, () => {
      this.alertService.setAlert('Failed to create Form', 'error', 'formBuilder');
    });
  }

  delete(row) {
    this.formBuilderQuestionService.delete(row.id);
  }

  openModal(row: FormBuilderQuestionDTO, isCreating: boolean) {
    const modalRef = this.modalService.openStackedModal(FormBuilderQuestionComponent, this.options, [
      {key: 'questionDTO', value: row},
      {key: 'isCreating', value: isCreating},
      {key: 'isEditable', value: this.isEditable},
      {key: 'formId', value: this.formEntity.id},
    ]);

    // refresh is the @Output
    this.componentInstanceSubscription = modalRef.componentInstance.refresh.subscribe(() => {
      this.fetch();
    });

    modalRef.result.then(reason => {
      // This runs when the modal is closed with the close() function
      if (reason === 'saved') {
        this.fetch();
      } else {
        this.fetch();
      }
    }).catch(() => {
      // This runs when the modal is dismissed (ie: by clicking the backdrop or esc key)
    });
  }

  private getParams(): Partial<FormBuilderDTO> {
    const funderId = this.sessionQuery.getUser().funderId;
    let params: any = {template: true};
    if (funderId) {
      params = {
        funder: {id: funderId},
        template: true
      };
    }
    return params;
  }
}
